import { OrderedList } from '@tiptap/extension-list';
import { mergeAttributes } from '@tiptap/core';
import type { ResolvedPos, Node as PMNode, NodeType } from '@tiptap/pm/model';
import { Plugin, PluginKey, Transaction } from '@tiptap/pm/state';
import { v4 as uuidv4 } from 'uuid';
import { TMap } from '@shared/types';
import { diffBounds, hasNodeOrMarkInRange, markSystemTr } from '../plugins/shared';
import type { TOlPattern } from './listPatterns';
import { setAttrsAtPos, getRootList, patchPatternTree, type TListHit } from './listUtils';
import getListPatternPlugin from '../plugins/getListPatternPlugin';

/** IMPORTANT: augment the commands for the node named 'orderedListWithStart' */
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    orderedListWithStart: {
      resumeOrderedList: () => ReturnType;
      restartOrderedList: () => ReturnType;
      setOrderedListStart: (start: number) => ReturnType;
      toggleOrderedListWithResume: () => ReturnType;
      setOrderedListPattern: (pattern: TOlPattern | null) => ReturnType;
    };
  }
}

/* ------------------------------------------------------------------ */
/* constants / types                                                   */
/* ------------------------------------------------------------------ */
const OL_WITH_START_NODE_NAME = 'orderedListWithStart';
type TManual = { pos: number; listId: string; start: number } | null;
type TState = { updatedTime: number; from: number | null; manual: TManual };
type TCounters = Map<string, number>;
type TPatch = { pos: number; attrs: TMap<string, unknown> };

/* ------------------------------------------------------------------ */
/* helpers                                                            */
/* ------------------------------------------------------------------ */

/**
 * Only consider FIRST-LEVEL ordered lists for “resume” logic.
 * NOTE: We purposely ignore nested lists for now. If we ever need nesting,
 * add a `level` param or a variant that accepts a target level.
 */
const findPrevList = (doc: PMNode, beforePos: number, listType: NodeType): TListHit => {
  let hit: TListHit = null;

  const isTopLevelAt = (pos: number): boolean => {
    const $pos = doc.resolve(pos); // resolve to the boundary before the node
    // If any ancestor is an ordered-list, then this node is nested.
    for (let d = 0; d <= $pos.depth; d++) {
      if ($pos.node(d).type === listType) return false;
    }
    return true;
  };

  doc.descendants((node, pos) => {
    if (pos >= beforePos) return false;
    if (node.type !== listType) return true;
    if (isTopLevelAt(pos)) hit = { node, pos };
    return true;
  });

  return hit;
};

const num = (v: unknown, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

/** Rename listId for all following segments of the same series */
const replaceListIdForward = (
  tr: Transaction,
  doc: PMNode,
  olType: NodeType,
  fromPosExclusive: number,
  oldId: string | null | undefined,
  newId: string
) => {
  if (!oldId || oldId === newId) return;
  doc.descendants((node, pos) => {
    if (pos <= fromPosExclusive) return true;
    if (node.type !== olType) return true;
    if (node.attrs.listId === oldId) {
      setAttrsAtPos(tr, pos, olType, { listId: newId });
    }
    return true;
  });
};

/* ------------------------------------------------------------------ */
/* diff-aware recalculation                                            */
/* ------------------------------------------------------------------ */

/** Seed counters up to a position, *respecting explicit start on the first segment of each series*. */
const seedCountersUpTo = (
  doc: PMNode,
  untilPos: number,
  olType: NodeType
): { counters: TCounters; needFullRecompute: boolean } => {
  const counters: TCounters = new Map();
  let needFull = false;

  doc.descendants((node, pos) => {
    if (pos >= untilPos) return false;
    if (node.type !== olType) return true;

    const listId = node.attrs.listId as string | null;
    if (!listId) {
      // Missing id before our start means we can't seed reliably → one-time full recompute.
      needFull = true;
      return false;
    }

    const first = !counters.has(listId);
    const consumed = node.childCount || 0;

    if (first) {
      const explicitStart = Math.max(1, num(node.attrs.start, 1));
      counters.set(listId, explicitStart - 1 + consumed);
    } else {
      counters.set(listId, (counters.get(listId) ?? 0) + consumed);
    }
    return true;
  });

  return { counters, needFullRecompute: needFull };
};

/**
 * Recalculate from `fromPos` onward.
 * Rule: for the *first* occurrence of a series we see, if it already has an explicit start, honor it.
 * Otherwise compute start = counters.get(id)+1.
 */
const recalcFrom = (doc: PMNode, fromPos: number, olType: NodeType, seed: TCounters): TPatch[] => {
  const counters = new Map(seed);
  const patches: TPatch[] = [];

  doc.descendants((node, pos) => {
    if (node.type !== olType) return true;
    if (pos < fromPos) return true;

    let listId = node.attrs.listId as string | null;
    if (!listId) {
      listId = uuidv4();
      patches.push({ pos, attrs: { listId } });
    }

    const first = !counters.has(listId);
    const existingStart = Math.max(1, num(node.attrs.start, 1));
    const consumed = node.childCount || 0;

    if (first && node.attrs.start != null) {
      // Respect explicit user start on the first segment of this series - but not below 1. Typing "0. " gives
      // start 0, which the shared schema rejects, so a saved list like that failed every AI Edit run.
      if (node.attrs.start !== existingStart) patches.push({ pos, attrs: { start: existingStart } });
      counters.set(listId, existingStart - 1 + consumed);
      return true;
    }

    // Compute from counters
    const soFar = counters.get(listId) ?? 0;
    const computedStart = Math.max(1, soFar + 1);
    if (existingStart !== computedStart) {
      patches.push({ pos, attrs: { start: computedStart } });
    }
    counters.set(listId, soFar + consumed);
    return true;
  });

  // Apply bottom-up so positions remain stable
  return patches.sort((a, b) => b.pos - a.pos);
};

/* ------------------------------------------------------------------ */
/* plugin: recalc ordered list starts on every meaningful change       */
/* ------------------------------------------------------------------ */

export const RECALC_OL_STARTS_PLUGIN_KEY = new PluginKey<TState>('ol-starts-recalc');

const getRecalcOlStartsPlugin = () =>
  new Plugin<TState>({
    key: RECALC_OL_STARTS_PLUGIN_KEY,

    state: {
      init() {
        return { updatedTime: -1, from: 0, manual: null };
      },
      apply(tr, value, oldState, newState) {
        // Ignore our own normalization transactions
        if (tr.getMeta(RECALC_OL_STARTS_PLUGIN_KEY) === 'normalize') return value;

        // pick up manual override from commands (or clear it)
        const manual = (tr.getMeta(RECALC_OL_STARTS_PLUGIN_KEY)?.manualStart as TManual) ?? null;

        if (!tr.docChanged && value.updatedTime >= 0 && !manual) return value;

        const olType = newState.schema.nodes[OL_WITH_START_NODE_NAME];
        if (!olType) return value;

        const diff = diffBounds(oldState, newState);
        if (!diff && !manual) return value;

        // A change touching no ordered list cannot move any start; skip the recalc walk.
        if (
          !manual &&
          diff &&
          !hasNodeOrMarkInRange(oldState.doc, diff.start, diff.oldEnd, OL_WITH_START_NODE_NAME) &&
          !hasNodeOrMarkInRange(newState.doc, diff.start, diff.newEnd, OL_WITH_START_NODE_NAME)
        ) {
          return value;
        }

        // Start from the previous list (for stability) and also ensure we include the manual pos if present.
        const prevList = diff ? findPrevList(newState.doc, diff.start, olType) : null;
        const from = Math.min(
          prevList ? prevList.pos : Number.POSITIVE_INFINITY,
          manual ? manual.pos : Number.POSITIVE_INFINITY,
          diff ? diff.start : Number.POSITIVE_INFINITY
        );

        return { updatedTime: Date.now(), from, manual };
      },
    },

    appendTransaction(_trs, oldState, newState) {
      const prev = RECALC_OL_STARTS_PLUGIN_KEY.getState(oldState);
      const next = RECALC_OL_STARTS_PLUGIN_KEY.getState(newState);
      if (!next || (prev && next.updatedTime === prev.updatedTime && prev.manual === next.manual)) {
        return null;
      }

      const olType = newState.schema.nodes[OL_WITH_START_NODE_NAME];
      if (!olType) return null;

      let from = next.from ?? 0;

      // Seed counters up to `from`. If earlier lists lack listId, recompute from 0 once.
      const { counters, needFullRecompute } = seedCountersUpTo(newState.doc, from, olType);
      if (needFullRecompute) from = 0;

      // Honor manual start (seed counter for that new listId)
      if (next.manual) {
        counters.set(next.manual.listId, Math.max(0, next.manual.start - 1));
      }

      const patches = recalcFrom(newState.doc, from, olType, counters);
      if (!patches.length) return null;

      const tr = newState.tr.setMeta(RECALC_OL_STARTS_PLUGIN_KEY, 'normalize');
      patches.forEach(({ pos, attrs }) => setAttrsAtPos(tr, pos, olType, attrs));
      return tr.steps.length ? markSystemTr(tr) : null;
    },
  });

/* ------------------------------------------------------------------ */
/* the OrderedListWithStart extension + commands                       */
/* ------------------------------------------------------------------ */

const OrderedListWithStart = OrderedList.extend({
  name: OL_WITH_START_NODE_NAME,

  addAttributes() {
    return {
      ...this.parent?.(),
      listId: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-list-id'),
        renderHTML: (attrs) => (attrs.listId ? { 'data-list-id': attrs.listId } : {}),
      },
      ilvl: {
        default: null,
        parseHTML: (el) => {
          const v = el.getAttribute('data-ilvl');
          return v != null ? Number(v) : null;
        },
        renderHTML: (attrs) => (attrs.ilvl != null ? { 'data-ilvl': String(attrs.ilvl) } : {}),
      },
      listPattern: {
        default: null, // null = default pattern (decimal-chain), no attribute emitted
        parseHTML: (el) => el.getAttribute('data-list-pattern') || null,
        renderHTML: (attrs) => (attrs.listPattern ? { 'data-list-pattern': attrs.listPattern } : {}),
      },
    };
  },

  renderHTML({ HTMLAttributes }) {
    const start = Number(HTMLAttributes.start ?? 1);
    const attrs: TMap<string, any> = { ...HTMLAttributes };
    if (Number.isFinite(start) && start > 1) {
      attrs.style = `${attrs.style || ''};--ol-start:${start - 1};`;
    }
    const { start: _omit, ...withoutStart } = attrs;
    return start === 1
      ? ['ol', mergeAttributes(this.options.HTMLAttributes, withoutStart), 0]
      : ['ol', mergeAttributes(this.options.HTMLAttributes, attrs), 0];
  },

  addCommands() {
    const listTypeName = this.name;
    const parent = this.parent?.() ?? {};

    const getCurrentList = ($from: ResolvedPos): TListHit => {
      for (let d = $from.depth; d >= 0; d--) {
        const node = $from.node(d) as PMNode;
        if (node.type.name === listTypeName) return { node, pos: $from.before(d) };
      }
      return null;
    };

    return {
      ...parent,

      resumeOrderedList:
        () =>
        ({ chain, state }) => {
          const cur = getCurrentList(state.selection.$from as ResolvedPos);
          if (!cur) return false;

          const prev = findPrevList(state.doc, cur.pos, state.schema.nodes[listTypeName]);
          if (!prev) return true;

          const prevStart = Math.max(1, num(prev.node.attrs.start, 1));
          const prevLen = prev.node.childCount || 0;
          const nextStart = Math.max(1, prevStart + prevLen);

          return chain()
            .command(({ tr }) =>
              setAttrsAtPos(tr, cur.pos, state.schema.nodes[listTypeName], {
                start: nextStart,
                listId: prev.node.attrs.listId || uuidv4(),
                listPattern: prev.node.attrs.listPattern || null,
              })
            )
            .run();
        },

      restartOrderedList:
        () =>
        ({ chain, state }) => {
          const cur = getCurrentList(state.selection.$from as ResolvedPos);
          if (!cur) return false;

          const oldId = cur.node.attrs.listId as string | null;
          const newId = uuidv4();

          return chain()
            .command(({ tr }) => {
              // mark manual start for plugin seeding
              tr.setMeta(RECALC_OL_STARTS_PLUGIN_KEY, { manualStart: { pos: cur.pos, listId: newId, start: 1 } });

              // current segment becomes new series with start=1
              setAttrsAtPos(tr, cur.pos, state.schema.nodes[listTypeName], { start: 1, listId: newId });

              // forward-rename remaining segments of old series to the new series id
              replaceListIdForward(tr, tr.doc, state.schema.nodes[listTypeName], cur.pos, oldId, newId);

              return true;
            })
            .run();
        },

      setOrderedListStart:
        (start: number) =>
        ({ chain, state }) => {
          const s = Math.max(1, num(start, 1));
          const cur = getCurrentList(state.selection.$from as ResolvedPos);
          if (!cur) return false;

          const oldId = cur.node.attrs.listId as string | null;
          const newId = uuidv4(); // new series boundary

          return chain()
            .command(({ tr }) => {
              tr.setMeta(RECALC_OL_STARTS_PLUGIN_KEY, { manualStart: { pos: cur.pos, listId: newId, start: s } });

              setAttrsAtPos(tr, cur.pos, state.schema.nodes[listTypeName], { start: s, listId: newId });

              replaceListIdForward(tr, tr.doc, state.schema.nodes[listTypeName], cur.pos, oldId, newId);

              return true;
            })
            .run();
        },

      toggleOrderedListWithResume:
        () =>
        ({ chain, state }) => {
          const wasActive = !!getRootList(state.selection.$from as ResolvedPos, listTypeName);
          return chain()
            .toggleOrderedList()
            .command(({ tr }) => {
              if (wasActive) return true;

              const selFrom = tr.selection.$from as ResolvedPos;
              let cur: { node: PMNode; pos: number } | null = null;
              for (let d = selFrom.depth; d >= 0; d--) {
                const node = selFrom.node(d) as PMNode;
                if (node.type.name === listTypeName) {
                  cur = { node, pos: selFrom.before(d) };
                  break;
                }
              }
              if (!cur) return true;

              const prev = findPrevList(tr.doc, cur.pos, state.schema.nodes[listTypeName]);
              if (!prev) {
                return setAttrsAtPos(tr, cur.pos, state.schema.nodes[listTypeName], {
                  start: 1,
                  listId: uuidv4(),
                });
              }

              const prevStart = Math.max(1, num(prev.node.attrs.start, 1));
              const prevLen = prev.node.childCount || 0;
              const nextStart = Math.max(1, prevStart + prevLen);

              return setAttrsAtPos(tr, cur.pos, state.schema.nodes[listTypeName], {
                start: nextStart,
                listId: prev.node.attrs.listId || uuidv4(),
                listPattern: prev.node.attrs.listPattern || null,
              });
            })
            .run();
        },

      setOrderedListPattern:
        (pattern: TOlPattern | null) =>
        ({ state, tr }) => {
          const root = getRootList(state.selection.$from as ResolvedPos, listTypeName);
          if (!root) return false;

          const olType = state.schema.nodes[listTypeName];
          const rootListId = root.node.attrs.listId as string | null;

          // Set pattern on root and all nested descendants
          patchPatternTree(tr, root.pos, root.node, olType, pattern);

          // Also propagate to other top-level segments sharing the same listId (resumed lists)
          if (rootListId) {
            tr.doc.descendants((node, pos) => {
              if (pos === root.pos) return true;
              if (node.type !== olType) return true;
              if (node.attrs.listId === rootListId) {
                patchPatternTree(tr, pos, node, olType, pattern);
              }
              return true;
            });
          }

          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    const listTypeName = this.name;
    return [getRecalcOlStartsPlugin(), getListPatternPlugin(listTypeName, 'ol-pattern-normalize')];
  },
});

export default OrderedListWithStart;
